// src/components/shared/Dialog.jsx
// Wiederverwendbarer Ersatz für window.confirm()/window.alert() - native Browser-Dialoge lassen
// sich nicht stylen und zeigen unschöne Zusatztexte ("localhost:3000 sagt:"). Ein einziger
// <DialogProvider> ganz oben in der Komponenten-Hierarchie (siehe App.js) verwaltet EINEN
// aktiven Dialog global. useDialog() liefert zwei Promise-basierte Funktionen, die sich wie die
// nativen Pendants anfühlen, aber die eigene, gestylte Box nutzen:
//
//   const { confirm, alert } = useDialog();
//   const bestaetigt = await confirm({ titel: 'Eintrag löschen', nachricht: '...', gefahr: true });
//   if (bestaetigt) { ... }
//
//   await alert({ nachricht: 'Vorgang abgeschlossen.' }); // löst nach Klick auf OK auf
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react'

const DialogContext = createContext(null)

function Dialog ({
  titel,
  nachricht,
  bestaetigenLabel,
  abbrechenLabel,
  nurOk,
  gefahr,
  abbrechenBetont,
  onBestaetigen,
  onAbbrechen
}) {
  return (
    <div className='dialog-overlay' onClick={onAbbrechen}>
      <div
        className='dialog-box'
        onClick={e => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
      >
        {titel && <h3 className='dialog-titel'>{titel}</h3>}
        {nachricht && <div className='dialog-nachricht'>{nachricht}</div>}
        <div className='dialog-aktionen'>
          {!nurOk && (
            <button
              type='button'
              className={abbrechenBetont ? 'btn btn-primary' : 'btn'}
              onClick={onAbbrechen}
              autoFocus={abbrechenBetont}
            >
              {abbrechenLabel}
            </button>
          )}
          <button
            type='button'
            className={
              gefahr
                ? 'btn btn-gefahr'
                : abbrechenBetont
                ? 'btn'
                : 'btn btn-primary'
            }
            onClick={onBestaetigen}
            autoFocus={!abbrechenBetont}
          >
            {bestaetigenLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function DialogProvider ({ children }) {
  const [dialog, setDialog] = useState(null)
  const resolveRef = useRef(null)

  const oeffneDialog = useCallback(konfiguration => {
    return new Promise(resolve => {
      resolveRef.current = resolve
      setDialog(konfiguration)
    })
  }, [])

  const schliessen = ergebnis => {
    setDialog(null)
    resolveRef.current?.(ergebnis)
    resolveRef.current = null
  }

  // Bestätigen/Abbrechen, z.B. Löschen: liefert true/false.
  const confirm = useCallback(
    ({
      titel = 'Bestätigen',
      nachricht,
      bestaetigenLabel = 'Bestätigen',
      abbrechenLabel = 'Abbrechen',
      gefahr = false,
      abbrechenBetont = false
    } = {}) =>
      oeffneDialog({
        titel,
        nachricht,
        bestaetigenLabel,
        abbrechenLabel,
        gefahr,
        abbrechenBetont,
        nurOk: false
      }),
    [oeffneDialog]
  )

  // Nur OK, z.B. reine Information/Fehlermeldung: löst nach Klick auf.
  const alert = useCallback(
    ({ titel = 'Hinweis', nachricht, okLabel = 'OK' } = {}) =>
      oeffneDialog({
        titel,
        nachricht,
        bestaetigenLabel: okLabel,
        nurOk: true
      }),
    [oeffneDialog]
  )

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      {dialog && (
        <Dialog
          {...dialog}
          onBestaetigen={() => schliessen(true)}
          onAbbrechen={() => schliessen(false)}
        />
      )}
    </DialogContext.Provider>
  )
}

export function useDialog () {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error(
      'useDialog() muss innerhalb eines <DialogProvider> verwendet werden.'
    )
  }
  return context
}

export default Dialog
